import React, { useState } from 'react';
import { Avatar, Button, Grid, Paper } from '@mui/material';

import { useTheme } from '@emotion/react';
import { styled } from '@mui/system';
import PasswordIcon from '@mui/icons-material/Password';
import LogoutIcon from '@mui/icons-material/Logout';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import OSAttributionsModal from '../components/OSAttributionsModal';

const useStyles = () => ({
  textTitle: {
    fontSize: '30px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  titleAbout: {
    fontSize: '24px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  textSubtitle: {
    fontSize: '15px',
    color: '#00658F',
  },
  textSubtitleAbout: {
    fontSize: '12px',
  },
  descriptionText: {
    fontSize: '18px',
  },
  descriptionTextAbout: {
    fontSize: '14px',
  },
  userData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '16px',
    marginLeft: 10,
  },
  userSubData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '14px',
    marginLeft: 10,
    color: '#41484D',
  },
  linkAbout: {
    color: '#00658F',
    fontSize: '14px',
  },
  changePassword: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginRight: 10,
    marginTop: 10,
  },
  signOut: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginTop: 10,
  },
  copyrightSubtitle: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  openSourceAttributionLink: {
    cursor: 'pointer',
  },
});

const SettingsSection = ({ user, signOut, desktopMode, versionText, aboutText }) => {
  const StyledPaper = styled(Paper)(() => ({
    backgroundColor: '#F3F3F6',
    color: 'black',
    borderRadius: '1rem',
    overflow: 'scroll',
    paddingTop: 0,
  }));
  const [openPasswordChange, setOpenPasswordChange] = useState(false);
  const [openOSAttributions, setOSAttributions] = useState(false);

  const theme = useTheme();
  const style = useStyles(theme);

  const isGoogleUser = () => {
    const identities =
      (user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.payload &&
        user.signInUserSession.idToken.payload.identities) ||
      [];

    for (let i = 0; i < identities.length; i++) {
      if (identities[i].providerName === 'Google') {
        return true;
      }
    }
  };

  const getUserGroup = () => {
    const groups = user.signInUserSession.accessToken.payload['cognito:groups'];
    for (let i = 0; i < groups.length; i++) {
      if (!(groups.includes('_google') && groups.includes('us-east-1'))) {
        return groups[i];
      }
    }
  };

  const handlePasswordDialogClose = () => {
    setOpenPasswordChange(false);
  };

  const handleOSAttributionModalClose = () => {
    setOSAttributions(false);
  };

  return (
    <>
      <ChangePasswordDialog
        open={openPasswordChange}
        handleClose={handlePasswordDialogClose}
        user={user}
      />
      <OSAttributionsModal open={openOSAttributions} handleClose={handleOSAttributionModalClose} />
      <Grid container spacing={0} alignItems="center">
        {desktopMode && (
          <div
            style={{
              flexDirection: 'column',
              alignItems: 'start',
              width: '100%',
            }}
          >
            <h2 style={style.textTitle}>Settings</h2>
          </div>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={5}>
            <StyledPaper
              sx={{
                my: 1,
                mx: 'auto',
                p: 2,
              }}
            >
              <div
                style={{
                  flexDirection: 'column',
                  alignItems: 'start',
                  width: '100%',
                }}
              >
                <h1 style={style.titleAbout}>About</h1>
                <p style={style.textSubtitleAbout}>{versionText}</p>
                <p style={style.descriptionTextAbout}>
                  {aboutText}
                </p>
                <p style={style.linkAbout}>
                  <a target="_blank" rel="noreferrer" href="/terms-and-conditions">
                    Terms and conditions...
                  </a>
                </p>
              </div>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            <StyledPaper
              sx={{
                my: 1,
                mx: 'auto',
                p: 2,
              }}
            >
              <div
                style={{
                  flexDirection: 'column',
                  alignItems: 'start',
                }}
              >
                <h1 style={style.titleAbout}>Account</h1>
                <Grid
                  container
                  style={{
                    margin: 20,
                    padding: 10,
                    backgroundColor: '#F9F9FC',
                    width: 'auto',
                    borderRadius: 5,
                  }}
                >
                  <Grid item xs={1}>
                    <Avatar
                      style={{
                        backgroundColor: 'transparent',
                        color: 'black',
                      }}
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <p style={style.userData}>
                      {user.attributes ? user.attributes.email : user.username}
                    </p>
                    <p style={style.userSubData}>{getUserGroup()}</p>
                  </Grid>
                </Grid>

                <Grid container>
                  <Grid>
                    {!isGoogleUser() && (
                      <Button
                        variant="outlined"
                        style={style.changePassword}
                        onClick={() => setOpenPasswordChange(true)}
                      >
                        <PasswordIcon style={{ marginRight: 10 }} /> Change password
                      </Button>
                    )}
                    <Button variant="outlined" style={style.signOut} onClick={() => signOut()}>
                      <LogoutIcon style={{ marginRight: 10 }} /> Sign out
                    </Button>
                  </Grid>
                </Grid>
              </div>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            <StyledPaper
              sx={{
                my: 1,
                mx: 'auto',
                p: 2,
              }}
            >
              <div
                style={{
                  flexDirection: 'column',
                  alignItems: 'start',
                  width: '100%',
                }}
              >
                <h1 style={style.titleAbout}>Copyright</h1>
                <p style={style.textSubtitleAbout}>
                  Copyright (c) 2025 Dobility, Inc., All rights reserved.
                </p>
                <p style={style.copyrightSubtitle}>Portions copyright</p>
                <p style={style.descriptionTextAbout}>
                  We are thankful to all the contributors to open source projects that were used in
                  the creation of this app.
                </p>
                <p style={style.linkAbout}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    style={style.openSourceAttributionLink}
                    onClick={() => setOSAttributions(true)}
                  >
                    Click to view open source attributions...
                  </a>
                </p>
              </div>
            </StyledPaper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SettingsSection;
